import type {
  SurveySectionKey,
  SurveySectionProps,
} from '@hotjar/feedback/domain/common/Wizard/Wizard';
import { useTranslation } from '@hotjar/translations';
import { BehaviorSummary } from '../Behavior';
import { TargetingSummary } from '../Targeting/TargetingSummary';
import { AppearanceSummary } from './AppearanceSummary';

export const useSectionsTitles = (
  sectionsProps: SurveySectionProps,
): Partial<Record<SurveySectionKey, { title: string; summary: string | JSX.Element }>> => {
  const { t } = useTranslation('feedback');

  const {
    name,
    location,
    questions,
    language,
    backgroundColor,
    buttonColor,
    isInvitationEnabled,
    isOffsite,
    displayCondition,
    displayDelay,
    persistCondition,
    activeForwardingNames,
    targeting,
  } = sectionsProps;

  return {
    name: { title: t('wizard.section.details.title'), summary: name },
    location: {
      title: t('wizard.section.location.title'),
      summary: typeof location === 'string' ? t(`survey.display-type.${location}`) : '',
    },
    questions: {
      title: t('wizard.section.questions.title'),
      summary:
        questions?.length > 0
          ? t('wizard.section.questions.summary', { count: questions.length })
          : '',
    },
    appearance: {
      title: t('wizard.section.appearance.title'),
      summary: (
        <AppearanceSummary
          languageCode={language}
          backgroundColor={backgroundColor}
          buttonColor={buttonColor}
        />
      ),
    },
    invitation: {
      title: t('wizard.section.invitation.title'),
      summary:
        isInvitationEnabled === true
          ? t('wizard.section.invitation.summary.on')
          : t('wizard.section.invitation.summary.off'),
    },
    targeting: {
      title:
        isOffsite === true
          ? t('wizard.section.targeting.invitation-title')
          : t('wizard.section.targeting.title'),
      summary: <TargetingSummary targeting={targeting} />,
    },
    behavior: {
      title: t('wizard.section.behavior.title'),
      summary: (
        <BehaviorSummary
          showDisplaySummary={isOffsite === false || isInvitationEnabled}
          displayCondition={displayCondition}
          displayDelay={displayDelay ?? 0}
          persistCondition={persistCondition}
        />
      ),
    },
    forwarding: {
      title: t('wizard.section.integrations.title'),
      summary:
        activeForwardingNames?.length > 0
          ? activeForwardingNames
          : t('wizard.section.integrations.not-set'),
    },
  };
};
